import { t } from "i18next";

import {
  AuctionItemMediaDTO,
  AuctionItemState,
  VehicleType
} from "@/src/types/types";
import { isDamageFrom } from "@/src/utils/auctionItemUtils";

export type ValidationBlockerType =
  | "general_images"
  | "damage_images"
  | "uploads_in_progress"
  | "aisa_processing"
  | "broken_images";

export interface ValidationBlocker {
  type: ValidationBlockerType;
  current?: number;
  required?: number;
}

export const collectMediaBlockers = (input: {
  generalImagesCount: number;
  requiredGeneralCount: number;
  damageImagesCount: number;
  requiredDamageCount: number;
  isAllUploadsComplete: boolean;
  isAisaProcessing: boolean;
  brokenImagesCount: number;
}): ValidationBlocker[] => {
  const blockers: ValidationBlocker[] = [];
  if (input.generalImagesCount < input.requiredGeneralCount) {
    blockers.push({
      type: "general_images",
      current: input.generalImagesCount,
      required: input.requiredGeneralCount
    });
  }
  if (
    input.requiredDamageCount > 0 &&
    input.damageImagesCount < input.requiredDamageCount
  ) {
    blockers.push({
      type: "damage_images",
      current: input.damageImagesCount,
      required: input.requiredDamageCount
    });
  }

  // Only block on upload/processing/broken state when required images are still outstanding;
  // optional (other_images) uploads in progress must not prevent progression.
  const requiredPending =
    Math.max(0, input.requiredGeneralCount - input.generalImagesCount) +
    Math.max(0, input.requiredDamageCount - input.damageImagesCount);

  if (requiredPending > 0) {
    if (!input.isAllUploadsComplete)
      blockers.push({ type: "uploads_in_progress" });
    if (input.isAisaProcessing) blockers.push({ type: "aisa_processing" });
    if (input.brokenImagesCount > 0)
      blockers.push({
        type: "broken_images",
        current: input.brokenImagesCount
      });
  }

  return blockers;
};

export const MEDIA_CATEGORIES = {
  GENERAL_IMAGES: "general_images",
  OTHER_IMAGES: "other_images",
  DAMAGE_IMAGES: "damage_images"
} as const;

const OLD_FROM_VALUES = new Set<string>([
  "front",
  "back",
  "frontAngle",
  "backAngle",
  "side",
  "sideRight",
  "sideLeft",
  "dashboard",
  "frontSeat",
  "engine",
  "trunk",
  "wheel",
  "interior",
  "frontTire",
  "backTire",
  "rightTrack",
  "leftTrack"
]);

export type MediaCategory =
  (typeof MEDIA_CATEGORIES)[keyof typeof MEDIA_CATEGORIES];

export type MediaItemRef = {
  from: string; // keep as string because damage "from" values are dynamic (prefix-matched)
  key: string;
  url: string;
};

export type MediaFormData = {
  totalImages?: number;
  generalImages?: number;
  damageImages?: number;
} & Partial<Record<MediaCategory, MediaItemRef | MediaItemRef[]>> &
  Partial<Record<string, MediaItemRef | MediaItemRef[] | number>>;

export interface StepStates {
  currentStep: AuctionItemState;
  nextStep: AuctionItemState;
  previousStep: AuctionItemState;
}

export const getStepStates = (vehicleType?: VehicleType): StepStates => {
  const isMoto = vehicleType === VehicleType.MOTO;
  const isCaravan = vehicleType === VehicleType.CARAVAN;

  let previousStep = AuctionItemState.STEP_ACCESSORIES;
  if (isMoto) {
    previousStep = AuctionItemState.MOTO_STEP_ACCESSORIES;
  }

  return {
    currentStep: isMoto
      ? AuctionItemState.MOTO_STEP_MEDIA
      : AuctionItemState.STEP_MEDIA,
    nextStep: isMoto
      ? AuctionItemState.MOTO_STEP_FREE_DESCRIPTION
      : AuctionItemState.STEP_FREE_DESCRIPTION,
    previousStep
  };
};

export const updateMediaItemsState = (
  currentMediaItems: AuctionItemMediaDTO[],
  newMediaItems: AuctionItemMediaDTO[],
  operation: "add" | "remove" = "add"
): AuctionItemMediaDTO[] => {
  if (operation === "add") {
    const uniqueNewMediaItems = newMediaItems.filter(
      (newItem) =>
        !currentMediaItems.some(
          (existingItem) => existingItem.key === newItem.key
        )
    );
    return [...currentMediaItems, ...uniqueNewMediaItems];
  } else {
    return currentMediaItems.filter(
      (item) =>
        !newMediaItems.some(
          (newItem) => newItem.from === item.from && newItem.key === item.key
        )
    );
  }
};

export const createGenericErrorMessage = () => ({
  text: t("general.somethingWentWrong"),
  variant: "error" as const
});

export const getDefaultVehicleType = (
  vehicleType?: VehicleType
): VehicleType => {
  return vehicleType || VehicleType.MOTO;
};

/**
 * Orders media items in the correct sequence:
 * 1. Main photo (first general image)
 * 2. Rest of general images
 * 3. Damaged images
 * 4. Other images
 */
export const orderMediaItems = (
  mediaItems: AuctionItemMediaDTO[]
): AuctionItemMediaDTO[] => {
  const generalImagesCategory = MEDIA_CATEGORIES.GENERAL_IMAGES;

  // Separate images by category
  const generalImages = mediaItems.filter(
    (item) => item.from === generalImagesCategory
  );
  const damagedImages = mediaItems.filter((item) => isDamageFrom(item.from));
  const otherImages = mediaItems.filter(
    (item) => item.from !== generalImagesCategory && !isDamageFrom(item.from)
  );

  // Order: main photo first, then rest of general images, then damaged, then other
  const orderedMediaItems = [
    ...generalImages, // Main photo will be first (index 0) due to isFirstItem logic
    ...damagedImages,
    ...otherImages
  ];

  return orderedMediaItems;
};

export const migrateOldImages = (
  mediaItems: AuctionItemMediaDTO[]
): AuctionItemMediaDTO[] => {
  return mediaItems.map((item) => {
    if (OLD_FROM_VALUES.has(item.from)) {
      return {
        ...item,
        from: MEDIA_CATEGORIES.GENERAL_IMAGES
      };
    }

    // Migrate old "additional_images" (other images) to new category
    if (item.from === "additional_images") {
      return {
        ...item,
        from: MEDIA_CATEGORIES.OTHER_IMAGES
      };
    }

    return item;
  });
};
